import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { DEFAULT_LANGUAGE } from "@modules/shared/localization/const/language";

export function NotFound({ children }: { children?: React.ReactNode }) {
  const params = useParams({
    from: "/$locale/_layout",
  });
  const locale = (params as { locale?: string }).locale ?? DEFAULT_LANGUAGE;
  const { t } = useTranslation(undefined, {
    lng: locale,
  });

  return (
    <div className="flex min-h-[70vh] w-full flex-col items-center justify-center px-4 py-16">
      {/* Decorative 404 */}
      <div className="relative mb-8 select-none">
        <span className="text-primary/10 text-[12rem] font-black leading-none sm:text-[16rem]">
          404
        </span>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-primary/5 flex h-28 w-28 items-center justify-center rounded-full sm:h-36 sm:w-36">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-primary h-14 w-14 sm:h-18 sm:w-18"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
              <path d="M8 11h6" />
            </svg>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex max-w-md flex-col items-center gap-4 text-center">
        {children || (
          <>
            <h1 className="text-2xlarge text-text-1 font-bold sm:text-3xlarge">
              {t("NotFound.title")}
            </h1>
            <p className="text-text-3 text-base leading-relaxed">
              {t("NotFound.description")}
            </p>
          </>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="border-border-5 text-text-1 hover:border-primary hover:text-primary rounded-lg border px-6 py-3 font-medium transition-colors"
          >
            {t("NotFound.goBack")}
          </button>
          <Link
            to="/$locale"
            params={{ locale }}
            className="bg-primary text-white hover:bg-primary-darker1 rounded-lg px-6 py-3 font-medium transition-colors"
          >
            {t("NotFound.startOver")}
          </Link>
        </div>

        <Link
          to="/$locale/products"
          params={{ locale }}
          className="text-primary hover:text-primary-darker1 mt-2 text-sm font-medium underline underline-offset-4"
        >
          {t("NotFound.searchProducts")}
        </Link>
      </div>
    </div>
  );
}
